import { graphql, withApollo } from 'react-apollo'
import { withRouter } from 'react-router'

import { withAnalyticsClient } from '@acorns/web-utils'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import withSpendClosedRedirect from 'components/HOCs/Spend/withSpendClosedRedirect'
import withSpendRedirect, {
  SpendStatus,
} from 'components/HOCs/Spend/withSpendRedirect'
import { SwitchType } from 'generated/types'
import { routes } from 'router/routes'

import userQuery from '../query.gql'
import searchQuery from './search.gql'
import { InnerProps, Step, subtitle } from './SearchComponents'
import { BackButtonTapped, SearchButtonTapped } from './segment'

export const switchUrl = (
  switchType: SwitchType,
  isWebView = false,
  isSubmittableEnabled = false,
) => {
  let page: string

  // Alternative logic for when the toggle is OFF
  if (switchType === 'submittable' && !isSubmittableEnabled) {
    page = 'pdf'
  } else {
    switch (switchType) {
      case 'pdf':
        page = 'pdf'
        break
      case 'online':
        page = 'online'
        break
      case 'submittable':
      case 'submittableCustom':
        page = 'submittable'
        break
      default:
        page = 'pdf'
    }
  }

  return `${isWebView ? '/wv/' : '/'}spend/direct-deposit/${page}`
}

export const withDirectDepositSearch = compose<InnerProps, {}>(
  withApollo,
  withRouter,
  withState('step', 'setStep', Step.Initial),
  withState('results', 'setResults', []),
  withState('searchTerm', 'setSearchTerm', ''),
  withState('trackingId', 'setTrackingId', ''),
  withState('searching', 'setSearching', false),
  withState(
    'heroSubtitle',
    'setHeroSubtitle',
    'Search for your employer, holding company, or payroll service.',
  ),
)

const enhance = compose<InnerProps, {}>(
  withSpendRedirect('/', SpendStatus.eligibility),
  withSpendClosedRedirect(),
  withAnalyticsClient,
  withDirectDepositSearch,
  graphql(userQuery),
  withProps(({ data }) => {
    if (!data.loading) {
      return {
        bankAccountId: pathOr('', ['spendAccounts', 0, 'id'], data),
      }
    } else {
      return {}
    }
  }),
  withProps<Partial<InnerProps>, InnerProps>((props) => {
    if (!props.data.loading) {
      return {
        bankAccountId: pathOr('', ['spendAccounts', 0, 'id'], props.data),
      }
    } else {
      return {}
    }
  }),
  withHandlers({
    handleSearch:
      ({
        setStep,
        setSearchTerm,
        client,
        setResults,
        bankAccountId,
        setTrackingId,
        setSearching,
        setHeroSubtitle,
        analytics,
      }) =>
      ({ searchTerm }) => {
        setSearchTerm(searchTerm)
        setStep(Step.Results)
        setSearching(true)

        analytics.track('Button Tapped', {
          ...SearchButtonTapped(searchTerm),
        })

        client
          .query({
            query: searchQuery,
            variables: {
              searchTerm,
              sessionId: analytics.getContext().sessionId,
              bankAccountId,
            },
          })
          .then(({ data }) => {
            setSearching(false)
            if (data) {
              setTrackingId(data.searchEmployers.trackingId)
              setResults(data.searchEmployers.results)
              setHeroSubtitle(
                subtitle(
                  Step.Results,
                  data.searchEmployers &&
                    data.searchEmployers.results.length > 0,
                ),
              )
            }
          })
      },
    handleAddEmployer:
      ({ history, searchTerm }) =>
      () => {
        history.push('/spend/direct-deposit/pdf', { employerName: searchTerm })
      },
    handleResultClick:
      ({ history }) =>
      (e) => {
        history.push(
          switchUrl(e.currentTarget.dataset.switchType, false, true),
          {
            employerName: e.currentTarget.dataset.employerName,
            employerId: e.currentTarget.dataset.employerId,
            ctaUrl: e.currentTarget.dataset.ctaUrl,
            clickSwitchTargetId: e.currentTarget.dataset.clickSwitchTargetId,
            switchType: e.currentTarget.dataset.switchType,
            requireAddress: e.currentTarget.dataset.requireAddress === 'true',
          },
        )
      },
    handleActionStripBack:
      ({ analytics, history, location }) =>
      () => {
        if (
          location?.state?.referrer === routes.spend.signupSuccess ||
          location?.state?.referrer === routes.spend.reissueSuccess
        ) {
          history.goBack()
        }
        analytics.track('Button Tapped', {
          ...BackButtonTapped,
        })
      },
  }),
)

export default enhance
